"use client";
import { Fragment, jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { useRenderAutocompleteOverride, useSearchFormAutocompleteConfig } from "../../context/SearchFormAutocompleteContext.js";
import { ConnectedAutocompleteSearchField } from "./ConnectedAutocompleteSearchField.js";
function resolveAutocompleteFieldOptions(options, slot) {
    if (!options) return {};
    return 'function' == typeof options ? options(slot) : options;
}
function FormAutocompleteField({ searchKey, label, typeAheadEndpoint, errorMessage, initialSelectedItem, onItemSelect, isDisabled, inputRef, renderTrigger, iconName, renderAutocomplete: renderAutocompleteProp, selectedFilterChipData, setSelectedFilterChipData, slotExtras, enableSearchOriginId, searchOriginResetGeneration, onRegisterFinalizeFeedback, ...options }) {
    const contextOverride = useRenderAutocompleteOverride();
    const renderAutocomplete = renderAutocompleteProp ?? contextOverride;
    const { analytics } = useSearchFormAutocompleteConfig();
    const selectedFilterChips = useMemo(()=>{
        if (!selectedFilterChipData) return;
        return new Set(Object.values(selectedFilterChipData).flatMap((values)=>[
                ...values
            ]));
    }, [
        selectedFilterChipData
    ]);
    const onFilterChipToggle = useCallback((item, selected)=>{
        const { filterType, code, name } = item;
        setSelectedFilterChipData?.((prev)=>{
            const next = new Set(prev[filterType]);
            const key = `${filterType}_${code}_${name}`;
            if (selected) next.add(key);
            else next.delete(key);
            return {
                ...prev,
                [filterType]: next
            };
        });
        analytics?.onFilterChipToggle?.(item, selected);
    }, [
        setSelectedFilterChipData,
        analytics
    ]);
    const onFilterChipsClear = useCallback(()=>{
        setSelectedFilterChipData?.((prev)=>Object.fromEntries(Object.keys(prev).map((key)=>[
                    key,
                    new Set()
                ])));
    }, [
        setSelectedFilterChipData
    ]);
    if (renderAutocomplete) return /*#__PURE__*/ jsx(Fragment, {
        children: renderAutocomplete({
            searchKey,
            label,
            typeAheadEndpoint,
            errorMessage,
            initialSelectedItem,
            onItemSelect,
            isDisabled,
            inputRef,
            renderTrigger,
            iconName,
            showCurrentLocation: options.showCurrentLocation,
            onGeolocationError: options.onGeolocationError,
            showFilterChips: options.showFilterChips,
            selectedFilterChipData,
            setSelectedFilterChipData,
            ...slotExtras
        })
    });
    return /*#__PURE__*/ jsx(ConnectedAutocompleteSearchField, {
        ref: inputRef ? (el)=>inputRef(el) : void 0,
        searchKey: searchKey,
        iconName: iconName,
        label: label,
        typeAheadEndpoint: typeAheadEndpoint,
        errorMessage: errorMessage,
        initialSelectedItem: initialSelectedItem,
        onItemSelect: onItemSelect,
        selectedFilterChips: selectedFilterChips,
        onFilterChipsClear: onFilterChipsClear,
        onFilterChipToggle: selectedFilterChipData ? onFilterChipToggle : void 0,
        enableSearchOriginId: enableSearchOriginId,
        searchOriginResetGeneration: searchOriginResetGeneration,
        onRegisterFinalizeFeedback: onRegisterFinalizeFeedback,
        ...options
    }, searchKey);
}
export { FormAutocompleteField, resolveAutocompleteFieldOptions };
